import React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { useData } from 'vike-react/useData'

import { ImgSanity, SanityTextRenderer } from '@slite/components'
import { ChevronLeftIcon } from '@slite/icons'

import { WebsiteCard } from '../../../components/Card/Card'
import {
  absoluteUrl,
  breadcrumbSchema,
  JsonLd,
  softwareApplicationSchema,
} from '../../../components/JsonLd'
import { Section } from '../../../components/Section'
import { SectionLastCTA } from '../../../components/SectionLastCTA'
import { SlitePage } from '../../../components/SlitePage'
import { WebsiteText } from '../../../components/WebsiteText'
import { useRoutes } from '../../../routes'
import { GetIntegrationButton } from '../GetIntegrationButton'
import { IntegrationMetadata } from '../IntegrationMetadata'
import type { Data } from './+data'

export default function IntegrationsShowPage() {
  const intl = useIntl()
  const routes = useRoutes()
  const { integration: data, allIntegrations } = useData<Data>()

  const getThumbnailSrc = () => {
    if (data.articleScreenshots && data.articleScreenshots.length > 0) {
      if (data.articleScreenshots.length === 2) {
        return data.articleScreenshots[1].screenshot.url
      }
      return data.articleScreenshots[0].screenshot.url
    }
    return ''
  }

  function getRelatedIntegrations(
    currentIntegration: Data['integration'],
    allIntegrations: Data['allIntegrations'],
    count: number
  ) {
    const relatedIntegrations = allIntegrations.filter(
      (integration) =>
        integration._id !== currentIntegration._id &&
        integration.categories.some((category) =>
          currentIntegration.categories.includes(category)
        )
    )

    return relatedIntegrations.slice(0, count)
  }

  const relatedIntegrations = getRelatedIntegrations(data, allIntegrations, 3)

  const thumbnailSrc = getThumbnailSrc()

  const integrationsLabel = intl.formatMessage({
    id: 'integrations.breadcrumb.integrations',
    defaultMessage: 'Integrations',
  })

  const softwareSchema = softwareApplicationSchema({
    name: data.name,
    description: data.articleDescription,
    url: absoluteUrl(routes.integrations.detail({ slug: data.slug.current })),
    imageUrl: data.logo.url,
    applicationCategory: data.categories[0],
  })

  const breadcrumbs = breadcrumbSchema({
    items: [
      { name: 'Slite', url: absoluteUrl('/') },
      {
        name: integrationsLabel,
        url: absoluteUrl(routes.integrations.index()),
      },
      {
        name: data.name,
        url: absoluteUrl(
          routes.integrations.detail({ slug: data.slug.current })
        ),
      },
    ],
  })

  return (
    <SlitePage
      canonicalUrl={routes.integrations.detail({ slug: data.slug.current })}
      background="moon-dust-lighter"
      title={data.openGraphTitle || data.name}
      description={data.metaDescription ?? data.articleDescription}
      ogImage={data.openGraphImage ? data.openGraphImage.url : thumbnailSrc}
      ogDescription={data.openGraphDescription || data.articleDescription}
    >
      <JsonLd data={softwareSchema} />
      <JsonLd data={breadcrumbs} />
      <Section background="moon-dust-lighter">
        <div>
          <a
            href={routes.integrations.index()}
            className="block shade-05 font-weight-semibold margin-bottom-32"
          >
            <FormattedMessage
              id="integration.page.backToIntegrations"
              defaultMessage="{arrow} Browse all"
              values={{
                arrow: (
                  <ChevronLeftIcon
                    size={12}
                    className="margin-right-4 margin-bottom-2"
                  />
                ),
              }}
            />
          </a>
        </div>
        <div className="grid-12 grid-column-gap-60 grid-column-gap-16-medium grid-column-gap-0-small">
          <div className="grid-span-3 padding-right-60-large padding-right-60-medium display-none-small">
            <ImgSanity
              className="radius-semilarger width-full margin-bottom-20 display-none-small"
              src={data.logo.url}
              alt={intl.formatMessage(
                {
                  id: 'integration.page.logoAlt',
                  defaultMessage: '{integrationName} logo',
                },
                {
                  integrationName: data.name,
                }
              )}
            />

            <GetIntegrationButton integrationWebsiteUrl={data.websiteUrl} />

            <IntegrationMetadata
              className="display-none-small"
              websiteUrl={data.websiteUrl}
              categories={data.categories}
            />
          </div>
          <div className="grid-span-9-large grid-span-9-medium grid-span-12-small">
            <div className="margin-bottom-40">
              <WebsiteText
                variant="heroTitle"
                className="margin-bottom-12 text-align-center-small"
              >
                {data.name}
              </WebsiteText>
              <WebsiteText
                variant="body"
                className="shade-05 font-weight-semibold margin-bottom-32 text-align-center-small margin-bottom-24-small font-size-4-small"
              >
                {data.articleDescription}
              </WebsiteText>

              <GetIntegrationButton
                integrationWebsiteUrl={data.websiteUrl}
                className="display-none-not-small"
              />

              <ImgSanity
                className="radius-semilarger width-full"
                src={thumbnailSrc}
                alt={intl.formatMessage(
                  {
                    id: 'integration.page.articleImageAlt',
                    defaultMessage: '{integrationName} integration screenshot',
                  },
                  {
                    integrationName: data.name,
                  }
                )}
              />
            </div>

            <h4 className="font-family-title font-size-3 font-weight-medium margin-bottom-8">
              <FormattedMessage
                id="integration.page.overview"
                defaultMessage="Overview"
              />
            </h4>

            <p className="margin-bottom-20">{data.articleOverview}</p>

            <h4 className="font-family-title font-size-3 font-weight-medium margin-bottom-8">
              <FormattedMessage
                id="integration.page.howItWorks"
                defaultMessage="How it works"
              />
            </h4>

            <div className="simple-rich-text simple-rich-text-sanity">
              <SanityTextRenderer
                variant="website"
                content={data.articleContent}
              />
            </div>
          </div>
        </div>

        <IntegrationMetadata
          className="margin-top-60 display-none-not-small"
          websiteUrl={data.websiteUrl}
          categories={data.categories}
        />
      </Section>
      {relatedIntegrations.length > 0 && (
        <Section background="moon-dust-lighter" containerTopPadding={0}>
          <h4 className="font-family-title font-size-3 font-weight-medium margin-bottom-20">
            <FormattedMessage
              id="integration.page.similar"
              defaultMessage="You might be interested in"
            />
          </h4>

          <div className="grid-12 grid-gap-24">
            {relatedIntegrations.map((integration) => (
              <WebsiteCard
                key={integration._id}
                hoverOutline={false}
                className="grid-span-4-large grid-span-6 padding-horizontal-24 padding-vertical-24 border-small border-alpha-shade-40 border-shade-05-on-hover"
                href={routes.integrations.detail({
                  slug: integration.slug.current,
                })}
              >
                <ImgSanity
                  style={{ width: 40 }}
                  className="radius-larger margin-bottom-8"
                  src={integration.logo.url}
                  alt={intl.formatMessage(
                    {
                      id: 'integrations.page.integrationLogoAlt',
                      defaultMessage: '{integrationName} logo',
                    },
                    {
                      integrationName: integration.name,
                    }
                  )}
                />
                <h3 className="font-family-title font-weight-medium font-size-3 margin-bottom-4 shade-05">
                  {integration.name}
                </h3>
                <WebsiteText variant="body" className="shade-20">
                  {integration.articleDescription}
                </WebsiteText>
              </WebsiteCard>
            ))}
          </div>
        </Section>
      )}

      <SectionLastCTA background="moon-dust-lighter" />
    </SlitePage>
  )
}
